import React from "react";
import { Activity, RotateCcw } from "lucide-react";

import type { WorkflowEdge, WorkflowNode } from "../api";

const W = 250;
const H = 80;
const COL_GAP  = 290;  // horizontal gap between columns — ample room for condition pills
const ROW_GAP  = 72;   // vertical gap between nodes in the same column
const PAD_X    = 56;
const PAD_Y    = 48;

/** Assign column (rank) to every node via BFS from roots, then vertically center shorter columns */
function computeLayout(
  nodes: WorkflowNode[],
  edges: WorkflowEdge[],
): Map<string, { x: number; y: number }> {
  const ids   = new Set(nodes.map((n) => n.node_id));
  const valid = edges.filter((e) => ids.has(e.from_node_id) && ids.has(e.to_node_id));

  // in-degree for root detection
  const inDeg = new Map<string, number>(nodes.map((n) => [n.node_id, 0]));
  for (const e of valid) inDeg.set(e.to_node_id, (inDeg.get(e.to_node_id) ?? 0) + 1);

  // adjacency list
  const adj = new Map<string, string[]>();
  for (const n of nodes) adj.set(n.node_id, []);
  for (const e of valid) adj.get(e.from_node_id)!.push(e.to_node_id);

  // BFS to assign rank (column index)
  const rank = new Map<string, number>();
  const queue: string[] = [];
  for (const n of nodes) {
    if ((inDeg.get(n.node_id) ?? 0) === 0) { rank.set(n.node_id, 0); queue.push(n.node_id); }
  }
  while (queue.length) {
    const cur = queue.shift()!;
    for (const next of (adj.get(cur) ?? [])) {
      const newRank = (rank.get(cur) ?? 0) + 1;
      if (!rank.has(next) || rank.get(next)! < newRank) {
        rank.set(next, newRank);
        queue.push(next);
      }
    }
  }
  // fallback for disconnected nodes
  for (const n of nodes) if (!rank.has(n.node_id)) rank.set(n.node_id, 0);

  // Group by column, sort by original y-position to preserve relative order
  const cols = new Map<number, WorkflowNode[]>();
  for (const n of nodes) {
    const c = rank.get(n.node_id) ?? 0;
    if (!cols.has(c)) cols.set(c, []);
    cols.get(c)!.push(n);
  }
  for (const col of cols.values()) {
    col.sort((a, b) =>
      (Number.isFinite(Number(a.position?.y)) ? Number(a.position.y) : 0) -
      (Number.isFinite(Number(b.position?.y)) ? Number(b.position.y) : 0),
    );
  }

  const sortedCols = [...cols.keys()].sort((a, b) => a - b);
  // Find maximum column height to vertically center shorter columns symmetrically
  const maxColH = Math.max(
    ...sortedCols.map((c) => {
      const col = cols.get(c)!;
      return col.length * H + (col.length - 1) * ROW_GAP;
    }),
  );

  const positions = new Map<string, { x: number; y: number }>();
  for (const colIdx of sortedCols) {
    const col = cols.get(colIdx)!;
    const x   = PAD_X + colIdx * (W + COL_GAP);
    const totalH = col.length * H + (col.length - 1) * ROW_GAP;
    const yOffset = Math.max(0, (maxColH - totalH) / 2);
    col.forEach((n, rowIdx) => {
      const y = PAD_Y + yOffset + rowIdx * (H + ROW_GAP);
      positions.set(n.node_id, { x, y });
    });
  }
  return positions;
}

interface Props {
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  nodeStats: Record<string, Record<string, number>>;
  selectedStages: string[];
  totalCallers: number;
  onToggleStage: (nodeId: string) => void;
  onReset: () => void;
}

function edgeLabel(edge: WorkflowEdge) {
  const condition = edge.conditions?.[0];
  const text = condition
    ? condition.operator === "equals"
      ? `${condition.field} = ${String(condition.value ?? "")}`
      : `${condition.field} ${condition.operator.replace(/_/g, " ")}`
    : "Default path";
  return edge.delay ? `${text} · ${edge.delay.value} ${edge.delay.unit}` : text;
}

function getEdgeColor(edge: WorkflowEdge, label: string): string {
  const l = label.toLowerCase();
  const val = String(edge.conditions?.[0]?.value ?? "").toLowerCase();
  if (
    l.includes("verified") ||
    l.includes("fast") ||
    l.includes("success") ||
    val === "yes" ||
    val === "true"
  ) {
    return "#10b981";
  }
  if (
    l.includes("fail") ||
    l.includes("reject") ||
    val === "no" ||
    val === "false"
  ) {
    return "#ef4444";
  }
  if (
    l.includes("missing") ||
    l.includes("correct") ||
    l.includes("attention")
  ) {
    return "#f59e0b";
  }
  if (l.includes("pending") || l.includes("scheduled")) {
    return "#3b82f6";
  }
  if (l.includes("active")) {
    return "#8b5cf6";
  }
  return "#64748b";
}

function getEdgeIcon(edge: WorkflowEdge, label: string): string {
  const l = label.toLowerCase();
  const val = String(edge.conditions?.[0]?.value ?? "").toLowerCase();
  if (val === "no" || val === "false" || l.includes("fail") || l.includes("reject")) return "✕ ";
  if (l.includes("missing") || l.includes("correct") || l.includes("attention")) return "⚠ ";
  if (l.includes("default")) return "→ ";
  return "✓ ";
}

/** Read-only runtime graph. Uses a hybrid HTML+SVG approach:
 *  - Edges are drawn in an absolutely-positioned SVG layer (Layer 1)
 *  - Nodes are rendered as absolutely-positioned HTML divs
 *  - Condition pills are rendered in top SVG layer (Layer 2, zIndex: 10)
 */
export function WorkflowRuntimeGraph({ nodes, edges, nodeStats, selectedStages, totalCallers, onToggleStage, onReset }: Props) {
  if (!nodes.length) return null;

  // Compute geometric layout (rank-based columns, vertically centered)
  const posMap = computeLayout(nodes, edges);
  const placed = nodes.map((node) => ({ ...node, ...posMap.get(node.node_id)! }));
  const byId   = new Map(placed.map((node) => [node.node_id, node]));
  const linked = edges.filter((edge) => byId.has(edge.from_node_id) && byId.has(edge.to_node_id));

  // Group outgoing edges by source node and sort by target Y to assign clean port offsets
  const outgoingByFrom = new Map<string, WorkflowEdge[]>();
  for (const edge of linked) {
    if (!outgoingByFrom.has(edge.from_node_id)) {
      outgoingByFrom.set(edge.from_node_id, []);
    }
    outgoingByFrom.get(edge.from_node_id)!.push(edge);
  }
  for (const list of outgoingByFrom.values()) {
    list.sort((a, b) => {
      const posA = posMap.get(a.to_node_id);
      const posB = posMap.get(b.to_node_id);
      return (posA?.y ?? 0) - (posB?.y ?? 0);
    });
  }

  // Pre-compute unified edge geometry for both SVG layers
  const edgeGeoms = linked.map((edge, i) => {
    const from = byId.get(edge.from_node_id)!;
    const to   = byId.get(edge.to_node_id)!;

    const outList = outgoingByFrom.get(edge.from_node_id) ?? [edge];
    const outCount = outList.length;
    const outIdx = Math.max(0, outList.indexOf(edge));

    let outYOffset = 0;
    if (outCount === 2) {
      outYOffset = outIdx === 0 ? -12 : 12;
    } else if (outCount >= 3) {
      const span = Math.min(36, H - 28);
      outYOffset = -span / 2 + (span / (outCount - 1)) * outIdx;
    }

    const x1 = from.x + W;
    const y1 = from.y + H / 2 + outYOffset;
    const x2 = to.x - 4;
    const y2 = to.y + H / 2;

    const dx = Math.max(Math.abs(x2 - x1) * 0.48, 60);
    const d = `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;

    const bx = (x1 + x2) / 2;
    const by = (y1 + y2) / 2;

    const label       = edgeLabel(edge);
    const selected    = selectedStages.includes(edge.to_node_id);
    const baseColor   = getEdgeColor(edge, label);
    const strokeColor = selected ? baseColor : "#94a3b8";
    const markerId    = `arr-${strokeColor.slice(1)}`;

    const icon        = getEdgeIcon(edge, label);
    const fullText    = `${icon}${label}`;
    const pillW       = Math.max(84, Math.round(fullText.length * 6.2 + 20));
    const pillH       = 24;
    const pillFill    = selected ? baseColor : "var(--color-elevated)";
    const pillText    = selected ? "#ffffff" : baseColor;
    const pillStroke  = selected ? baseColor : `${baseColor}99`;

    return {
      edge,
      i,
      d,
      bx,
      by,
      strokeColor,
      markerId,
      selected,
      fullText,
      pillW,
      pillH,
      pillFill,
      pillText,
      pillStroke,
    };
  });

  const canvasWidth  = Math.max(920, ...placed.map((n) => n.x + W + PAD_X));
  const canvasHeight = Math.max(300, ...placed.map((n) => n.y + H + PAD_Y));

  return (
    <section className="rounded-2xl border border-line bg-elevated shadow-sm" data-testid="workflow-runtime-graph">
      <header className="flex flex-wrap items-center justify-between gap-4 border-b border-line px-6 py-4">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-soft text-brand">
            <Activity className="h-5 w-5" />
          </span>
          <div>
            <h2 className="text-base font-bold text-ink tracking-tight">Workflow Graph Runtime Overlay</h2>
            <p className="text-xs text-ink-3 mt-0.5">Live occupancy and outcomes mapped onto the published workflow.</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="hidden text-xs text-ink-3 md:inline">Click any node or path pill to filter callers</span>
          {selectedStages.length > 0 && (
            <button
              type="button"
              onClick={onReset}
              className="inline-flex items-center gap-2 rounded-lg border border-line bg-elevated px-4 py-2 text-sm font-semibold text-ink-2 hover:bg-soft transition-colors"
            >
              <RotateCcw className="h-4 w-4" />
              Reset Filters
            </button>
          )}
        </div>
      </header>

      {/* Graph canvas — HTML position:relative container with SVG edge layer + HTML node divs */}
      <div
        data-testid="workflow-runtime-canvas"
        className="overflow-x-auto overflow-y-hidden border-t border-line bg-canvas transition-colors"
        style={{
          minHeight: 320,
        }}
      >
        <div
          style={{
            position: 'relative',
            width: canvasWidth,
            height: canvasHeight,
            minWidth: canvasWidth,
            minHeight: canvasHeight,
          }}
        >
          {/* ── Layer 1: bezier lines + arrowheads — BEHIND nodes ── */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }}
          >
            <defs>
              <pattern id="runtime-grid" width="32" height="32" patternUnits="userSpaceOnUse">
                <circle cx="1.5" cy="1.5" r="1" fill="var(--color-line)" opacity="0.6" />
              </pattern>
              {(['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#64748b', '#94a3b8'] as const).map((c) => (
                <marker key={c} id={`arr-${c.slice(1)}`} markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
                  <polygon points="0,1 7,4 0,7" fill={c} />
                </marker>
              ))}
            </defs>
            <rect width="100%" height="100%" fill="url(#runtime-grid)" />

            {edgeGeoms.map((geom) => (
              <g key={geom.edge.edge_id ?? geom.i}>
                <path d={geom.d} fill="none" stroke="transparent" strokeWidth={14} />
                <path
                  d={geom.d}
                  fill="none"
                  stroke={geom.strokeColor}
                  strokeWidth={geom.selected ? 2.5 : 1.8}
                  markerEnd={`url(#${geom.markerId})`}
                  style={{ transition: 'stroke 0.15s' }}
                />
              </g>
            ))}
          </svg>

          {/* ── Node HTML layer ── */}
          {placed.map((node) => {
            const stats = nodeStats[node.node_id] || {};
            const total = stats.total || 0;
            const active = (stats.active || 0) + (stats.scheduled || 0);
            const completed = stats.completed || 0;
            const percent = totalCallers ? Math.round(total * 100 / totalCallers) : 0;
            const selected = selectedStages.includes(node.node_id);
            const title = node.label || node.agent_name || (node.agent_id ? "Workflow step" : "Trigger");

            const colorScheme = active ? 'blue' : completed ? 'green' : 'gray';
            const colorClasses = {
              blue: { text: '#2563eb' },
              green: { text: '#16a34a' },
              gray: { text: '#6b7280' },
            };
            const colors = colorClasses[colorScheme];

            const nodeType = node.node_type || 'call';
            type TC = { icon: React.ReactNode; accent: string; iconBg: string; label: string };
            const typeConfig: Record<string, TC> = {
              call: {
                accent: '#f97316', iconBg: 'rgba(249, 115, 22, 0.14)', label: 'Voice',
                icon: <svg viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.41 2 2 0 0 1 3.6 1.25h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.84a16 16 0 0 0 6.29 6.29l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" /></svg>,
              },
              email: {
                accent: '#6366f1', iconBg: 'rgba(99, 102, 241, 0.14)', label: 'Email',
                icon: <svg viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>,
              },
              whatsapp: {
                accent: '#22c55e', iconBg: 'rgba(34, 197, 94, 0.14)', label: 'WhatsApp',
                icon: <svg viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>,
              },
              sms: {
                accent: '#0ea5e9', iconBg: 'rgba(14, 165, 233, 0.14)', label: 'SMS',
                icon: <svg viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /><line x1="9" y1="10" x2="15" y2="10" /><line x1="9" y1="14" x2="12" y2="14" /></svg>,
              },
            };
            const tc: TC = typeConfig[nodeType] ?? typeConfig.call;
            const accentColor = selected ? tc.accent : (active ? tc.accent : 'var(--color-line)');

            return (
              <div
                key={node.node_id}
                onClick={() => onToggleStage(node.node_id)}
                style={{
                  position: 'absolute', left: node.x, top: node.y, width: W, height: H,
                  borderRadius: 12, background: 'var(--color-elevated)',
                  border: `1px solid ${selected ? tc.accent : 'var(--color-line)'}`,
                  boxShadow: selected ? `0 0 0 2px ${tc.accent}40, 0 4px 12px rgba(0,0,0,0.25)` : '0 2px 8px rgba(0,0,0,0.12)',
                  cursor: 'pointer', boxSizing: 'border-box', fontFamily: 'Inter, sans-serif',
                  display: 'flex', alignItems: 'stretch', overflow: 'hidden',
                }}
              >
                <div style={{ width: 4, flexShrink: 0, background: accentColor, borderRadius: '12px 0 0 12px' }} />
                <div style={{ width: 42, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: tc.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 }}>
                    {tc.icon}
                  </div>
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0, paddingRight: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span
                      title={title}
                      style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {title}
                    </span>
                    <span style={{ fontSize: 9, fontWeight: 600, color: tc.accent, background: tc.iconBg, borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {tc.label}
                    </span>
                  </div>
                  <span style={{ fontSize: 10, color: 'var(--color-ink-3)', marginTop: 2, whiteSpace: 'nowrap' }}>
                    <span style={{ color: colors.text }}>{percent}%</span>
                    {' · '}
                    <span style={{ color: 'var(--color-info)' }}>{active} Live</span>
                  </span>
                  <div style={{ marginTop: 5, height: 2, borderRadius: 1, background: 'var(--color-line)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.max(1, percent)}%`, borderRadius: 1, background: tc.accent }} />
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', paddingRight: 10, flexShrink: 0 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700,
                    background: active ? tc.iconBg : 'var(--color-soft)',
                    color: active ? tc.accent : 'var(--color-ink-2)',
                    border: `1px solid ${active ? tc.accent + '40' : 'var(--color-line)'}`,
                    borderRadius: 20, padding: '3px 8px', whiteSpace: 'nowrap',
                  }}>
                    {total.toLocaleString()} Callers
                  </span>
                </div>
              </div>
            );
          })}

          {/* ── Layer 2: pill labels — ON TOP of nodes (z-index: 10) ── */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible', zIndex: 10 }}
          >
            {edgeGeoms.map((geom) => (
              <g
                key={geom.edge.edge_id ?? geom.i}
                style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                onClick={() => onToggleStage(geom.edge.to_node_id)}
              >
                <rect
                  x={geom.bx - geom.pillW / 2}
                  y={geom.by - geom.pillH / 2}
                  width={geom.pillW}
                  height={geom.pillH}
                  rx={geom.pillH / 2}
                  fill={geom.pillFill}
                  stroke={geom.pillStroke}
                  strokeWidth={1.5}
                  style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.08))' }}
                />
                <text
                  x={geom.bx}
                  y={geom.by + 3.5}
                  textAnchor="middle"
                  fontFamily="Inter, sans-serif"
                  fontSize={10}
                  fontWeight={700}
                  fill={geom.pillText}
                >
                  {geom.fullText}
                </text>
              </g>
            ))}
          </svg>

        </div>
      </div>

      <footer style={{ display: "flex", minHeight: 40, alignItems: "center", gap: 20, padding: "10px 20px", borderTop: "1px solid var(--color-line)", fontSize: 10, color: "var(--color-ink-3)" }}>
        <strong>Runtime status</strong><span>Active</span><span>Completed</span><span>Needs attention</span><span style={{ marginLeft: "auto" }}>{placed.length} nodes · {linked.length} transitions</span>
      </footer>
    </section>
  );
}